import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Package, MapPin, Clock, XCircle, CheckCircle2, Sparkles, Star, MessageCircle, CalendarClock } from 'lucide-react';
import { ChatDrawer } from '@/components/common/ChatDrawer';

const STATUS_LABELS = {
    requested: { label: 'Solicitado', tone: 'bg-blue-100 text-blue-800' },
    picked_up: { label: 'Recolectado', tone: 'bg-sky-100 text-sky-800' },
    processing: { label: 'En proceso', tone: 'bg-amber-100 text-amber-800' },
    ready: { label: 'Listo', tone: 'bg-lime-100 text-lime-800' },
    out_for_delivery: { label: 'En entrega', tone: 'bg-violet-100 text-violet-800' },
    delivered: { label: 'Entregado', tone: 'bg-emerald-100 text-emerald-800' },
    cancelled: { label: 'Cancelado', tone: 'bg-rose-100 text-rose-800' },
};
const PROGRESS = ['requested', 'picked_up', 'processing', 'ready', 'out_for_delivery', 'delivered'];

export default function DashboardServiceOrders() {
    const [orders, setOrders] = useState([]);
    const [chatOrder, setChatOrder] = useState(null);
    const [rateOrder, setRateOrder] = useState(null);
    const [rescheduleOrder, setRescheduleOrder] = useState(null);
    const [rating, setRating] = useState({ stars: 5, comment: '' });
    const [resch, setResch] = useState({ pickup_date: '', pickup_slot: 'morning' });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/me/services/orders');
            setOrders(data.items || []);
        } catch (e) { toast.error('Error al cargar'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const cancel = async (id) => {
        if (!window.confirm('¿Cancelar pedido? Si ya pagaste, tu OkWallet será reembolsada.')) return;
        try {
            await api.post(`/services/orders/${id}/cancel`);
            toast.success('Pedido cancelado');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const submitRating = async () => {
        setBusy(true);
        try {
            await api.post(`/services/orders/${rateOrder.id}/rate`, rating);
            toast.success('¡Gracias por calificar!');
            setRateOrder(null);
            setRating({ stars: 5, comment: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const submitReschedule = async () => {
        setBusy(true);
        try {
            await api.post(`/services/orders/${rescheduleOrder.id}/reschedule`, resch);
            toast.success('Recolección reprogramada');
            setRescheduleOrder(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="dashboard-service-orders">
            <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Mis servicios contratados</h1>
            </div>
            {orders.length === 0 && (
                <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
                    Aún no has solicitado servicios. Explora <a href="/servicios" className="text-[hsl(var(--primary))] underline">Lavandería / Tintorería</a>.
                </CardContent></Card>
            )}
            {orders.map(o => {
                const meta = STATUS_LABELS[o.status] || STATUS_LABELS.requested;
                const step = PROGRESS.indexOf(o.status);
                return (
                    <Card key={o.id} data-testid={`buyer-svc-order-${o.id}`}>
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-semibold">#{o.id.slice(0, 8)}</div>
                                        <Badge className={meta.tone}>{meta.label}</Badge>
                                        <Badge variant="outline">{o.kind === 'drycleaning' ? 'Tintorería' : 'Lavandería'}</Badge>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 text-xs text-muted-foreground">
                                        <div className="flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5" />{o.pickup_address}</div>
                                        <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{o.pickup_date} · {o.pickup_slot === 'morning' ? 'Mañana' : 'Tarde'}</div>
                                        <div className="flex items-center gap-1"><Package className="h-3 w-3" />{o.items?.length} · <strong>${o.total}</strong></div>
                                    </div>
                                    {o.rating && (
                                        <div className="mt-2 flex items-center gap-2 text-xs">
                                            <div className="flex">
                                                {[1, 2, 3, 4, 5].map(n => (
                                                    <Star key={n} className={`h-3.5 w-3.5 ${n <= o.rating.stars ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
                                                ))}
                                            </div>
                                            {o.rating.comment && <span className="text-muted-foreground italic">"{o.rating.comment}"</span>}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setChatOrder(o)} data-testid={`svc-chat-${o.id}`}>
                                            <MessageCircle className="h-4 w-4 mr-1" />Chat
                                        </Button>
                                        {o.status === 'requested' && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => { setResch({ pickup_date: o.pickup_date, pickup_slot: o.pickup_slot }); setRescheduleOrder(o); }} data-testid={`svc-reschedule-${o.id}`}>
                                                    <CalendarClock className="h-4 w-4 mr-1" />Reprogramar
                                                </Button>
                                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(o.id)} data-testid={`svc-cancel-${o.id}`}>
                                                    <XCircle className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                    {o.status === 'delivered' && !o.rating && (
                                        <Button size="sm" onClick={() => setRateOrder(o)} data-testid={`svc-rate-${o.id}`}>
                                            <Star className="h-4 w-4 mr-1" />Calificar
                                        </Button>
                                    )}
                                    {o.status === 'delivered' && o.rating && (
                                        <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" />Completado</Badge>
                                    )}
                                </div>
                            </div>
                            {step >= 0 && (
                                <div className="mt-3">
                                    <div className="flex items-center gap-1">
                                        {PROGRESS.map((s, i) => (
                                            <div key={s} className={`h-1.5 flex-1 rounded ${i <= step ? 'bg-[hsl(var(--primary))]' : 'bg-slate-200'}`} />
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-6 gap-1 text-[9px] text-center text-muted-foreground mt-1">
                                        {PROGRESS.map(s => <div key={s} className={o.status === s ? 'font-semibold text-foreground' : ''}>{STATUS_LABELS[s].label}</div>)}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                );
            })}

            {/* Chat drawer */}
            {chatOrder && (
                <ChatDrawer
                    open={!!chatOrder}
                    onOpenChange={(v) => !v && setChatOrder(null)}
                    mode="order"
                    orderId={chatOrder.id}
                    title={`Chat de pedido #${chatOrder.id.slice(0, 8)}`}
                />
            )}

            {/* Rating dialog */}
            <Dialog open={!!rateOrder} onOpenChange={(v) => !v && setRateOrder(null)}>
                <DialogContent data-testid="svc-rate-dialog">
                    <DialogHeader>
                        <DialogTitle>Califica tu servicio</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-center gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} type="button" onClick={() => setRating(r => ({ ...r, stars: n }))} data-testid={`svc-rate-star-${n}`}>
                                    <Star className={`h-9 w-9 transition ${n <= rating.stars ? 'text-amber-500 fill-amber-500' : 'text-slate-300 hover:text-amber-300'}`} />
                                </button>
                            ))}
                        </div>
                        <div>
                            <Label>Comentario (opcional)</Label>
                            <Textarea rows={3} value={rating.comment} onChange={(e) => setRating(r => ({ ...r, comment: e.target.value }))} placeholder="¿Cómo fue tu experiencia?" data-testid="svc-rate-comment" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setRateOrder(null)}>Cancelar</Button>
                        <Button onClick={submitRating} disabled={busy} data-testid="svc-rate-submit">{busy ? 'Enviando…' : 'Publicar calificación'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reschedule dialog */}
            <Dialog open={!!rescheduleOrder} onOpenChange={(v) => !v && setRescheduleOrder(null)}>
                <DialogContent data-testid="svc-reschedule-dialog">
                    <DialogHeader>
                        <DialogTitle>Reprogramar recolección</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nueva fecha</Label>
                                <Input type="date" value={resch.pickup_date} onChange={(e) => setResch(r => ({ ...r, pickup_date: e.target.value }))} data-testid="svc-resch-date" />
                            </div>
                            <div>
                                <Label>Horario</Label>
                                <Select value={resch.pickup_slot} onValueChange={(v) => setResch(r => ({ ...r, pickup_slot: v }))}>
                                    <SelectTrigger data-testid="svc-resch-slot"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="morning">Mañana (9-13)</SelectItem>
                                        <SelectItem value="afternoon">Tarde (14-18)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
                            Solo puedes reprogramar antes de que la ropa sea recolectada. El prestador recibirá una notificación.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setRescheduleOrder(null)}>Cancelar</Button>
                        <Button onClick={submitReschedule} disabled={busy || !resch.pickup_date} data-testid="svc-resch-submit">{busy ? 'Guardando…' : 'Confirmar cambio'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
